import { useTranslation } from 'next-i18next';

import React, { useMemo } from 'react';

import { Box, Separator, VStack } from '@chakra-ui/react';

import { AggregateRatingType } from '@nocowanie/core/enums';
import { ScrollableWrapper } from '@nocowanie/common-ui/components/molecules/scrollable-wrapper';

import * as accommodationStaticSlice from '@app/state/slices/accommodation-static.slice';
import { useAppSelector } from '@app/state/state';

import { ReviewSummaryProps } from './review-summary.props';

export const ReviewSummary = ({ withSidebar = false, ...props }: ReviewSummaryProps) => {
    const { t } = useTranslation('accommodation', {
        keyPrefix: 'reviews',
    });

    const { aggregateRating } = useAppSelector(
        accommodationStaticSlice.selectors.selectReviewsData,
    );

    const aggregateRatingItems = useMemo(() => {
        if (!aggregateRating) {
            return;
        }

        return Object.entries(aggregateRating).filter(
            ([label, value]) =>
                !(
                    label === AggregateRatingType.Average ||
                    label === AggregateRatingType.Convenience ||
                    !value
                ),
        );
    }, [aggregateRating]);

    const scrollableStyles = {
        maxW: { base: '100vw', md: '100%' },
        w: {
            base: '100vw',
            md: 'min-content',
        },
        gridAutoColumns: withSidebar ? '1fr auto' : 'max-content',
        gap: 3,
        scrollSnapType: 'none',
        ml: 0,
        pt: 0,
        px: { base: withSidebar ? 6 : 4, md: 0 },
    };

    return (
        <Box
            mx={{
                base: withSidebar ? -6 : '-gutterWidth',
                md: 0,
            }}
            {...props}
        >
            <ScrollableWrapper scrollableContainerProps={scrollableStyles}>
                {aggregateRatingItems?.map(([label, value], index) => {
                    return (
                        <React.Fragment key={index}>
                            <VStack gap={1} flexWrap={'wrap'} data-testid={'aggregate-rating-item'}>
                                <Box>
                                    <Box
                                        as={'span'}
                                        fontWeight={'bold'}
                                        fontSize={'lg'}
                                        color={'secondary.solid'}
                                    >
                                        {value}
                                    </Box>
                                    <Box as={'span'} fontSize={'sm'} color={'gray.500'}>
                                        /10
                                    </Box>
                                </Box>
                                <Box as={'span'} fontSize={'sm'}>
                                    {`${t(`aggregateRating.${label}`)}`}
                                </Box>
                            </VStack>
                            {index < aggregateRatingItems.length - 1 ? (
                                <Separator orientation={'vertical'} />
                            ) : null}
                        </React.Fragment>
                    );
                })}
            </ScrollableWrapper>
        </Box>
    );
};
